import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Drawer from './Drawer';
import Header from './Header';
import Footer from './Footer';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';
import Breadcrumbs from 'components/@extended/Breadcrumbs';
import { SystemShortcutsProvider } from 'contexts/SystemShortcutsContext';
import AddCustomer from 'sections/apps/customer/AddCustomer';
import AuthGuard from 'utils/route-guard/AuthGuard';
import SetupGuard from 'utils/route-guard/SetupGuard';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';

// ==============================|| MAIN LAYOUT ||============================== //

export default function DashboardLayout() {
  const { pathname } = useLocation();
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const { state } = useConfig();

  const isContainer = state.container;
  const isHorizontal = state.menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // set media wise responsive drawer
  useEffect(() => {
    if (state.menuOrientation !== MenuOrientation.MINI_VERTICAL) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  if (menuMasterLoading) return <Loader />;

  return (
    <AuthGuard>
      <SetupGuard>
        <SystemShortcutsProvider>
          <Box sx={{ display: 'flex', width: '100%' }}>
            <Header />
            {!isHorizontal ? <Drawer /> : <HorizontalBar />}

            <Box component="main" sx={{ width: 'calc(100% - 260px)', flexGrow: 1, p: { xs: 2, sm: 3 } }}>
              <Toolbar sx={{ mt: isHorizontal ? 8 : 'inherit' }} />
              <Container
                maxWidth={isContainer ? 'xl' : false}
                sx={{
                  ...(isContainer && { px: { xs: 0, sm: 2 } }),
                  position: 'relative',
                  minHeight: 'calc(100vh - 110px)',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                {!pathname.startsWith('/account') && pathname !== '/apps/profiles/account/my-account' && <Breadcrumbs />}
                <Outlet />
                <Footer />
              </Container>
            </Box>
            <AddCustomer />
          </Box>
        </SystemShortcutsProvider>
      </SetupGuard>
    </AuthGuard>
  );
}
